/**
 * Step 12 — Household & Economic Profile
 *
 * Fields: maritalStatus, familyMembers, [14 link sub-step fields gated on
 * familyMembers], numberOfDependents, housingType, householdIncome,
 * bplCard, digitalAccess, migrationStatus, landOwnership, primaryIncomeSource
 *
 * Family member linking:
 *   After picking familyMembers (sub-step 2), up to 14 conditional sub-steps
 *   appear — one per family-member type the user selected. Each sub-step
 *   shows a search UI (by name / email / phone) and lets the user link an
 *   existing platform account. Single-instance relations (mother / father
 *   / step_*) save as a single link object; multi-instance relations
 *   (siblings / children / spouse / other) save as an array.
 *
 *   The schema accepts these fields here so they can be validated at save
 *   time. The same data is also written to the User collection's
 *   `relations` sub-doc on save (indexed for graph traversal).
 *
 * @module udp-schema/step-12
 */

import { z } from 'zod';

// ─── Family-link sub-schema ─────────────────────────────────────────────────

/**
 * One family-link entry. `_id` is server-assigned on insert. Both `linkedUserId`
 * and `cachedName` are written together (per the "save id + name" decision —
 * id for joins / graph traversal, name for display without an extra lookup).
 *
 * `mutuallyConfirmed` flips to true when the *other* user adds a reciprocal
 * relation pointing back at this user (computed server-side on save).
 *
 * `verified` / `verifiedAt` / `verifiedBy` are populated when the initiating
 * volunteer marks the family-relations field group as verified per the
 * member's official ID (Phase 4 of the rollout).
 */
const familyLinkSchema = z.object({
  _id: z.string().optional(),
  relation: z.enum([
    'mother',
    'step_mother',
    'father',
    'step_father',
    'wife_husband',
    'blood_sister',
    'half_sister',
    'step_sister',
    'blood_brother',
    'half_brother',
    'step_brother',
    'daughter',
    'son',
    'other',
  ]),
  linkedUserId: z.string(),
  cachedName: z.string(),
  cachedAvatarUrl: z.string().optional(),
  mutuallyConfirmed: z.boolean().default(false),
  verified: z.boolean().default(false),
  verifiedAt: z.coerce.date().optional(),
  verifiedBy: z.string().optional(),
  createdAt: z.coerce.date().optional(),
});

// ─── Main Step 12 Schema ────────────────────────────────────────────────────

export const step12HouseholdSchema = z.object({
  /** Marital status */
  maritalStatus: z
    .enum(['single', 'married', 'divorced', 'widowed', 'separated', 'prefer_not_to_say'])
    .optional(),

  /** Family members present (multi-select string array) */
  familyMembers: z.array(z.string().trim()).optional(),

  // ── Family-link fields (gated on familyMembers via includesAny) ──
  // Single-instance relations: at most one link
  linkedMother: familyLinkSchema.optional(),
  linkedStepMother: familyLinkSchema.optional(),
  linkedFather: familyLinkSchema.optional(),
  linkedStepFather: familyLinkSchema.optional(),

  // Multi-instance relations: arrays
  linkedSpouses: z.array(familyLinkSchema).optional(),
  linkedBloodSisters: z.array(familyLinkSchema).optional(),
  linkedHalfSisters: z.array(familyLinkSchema).optional(),
  linkedStepSisters: z.array(familyLinkSchema).optional(),
  linkedBloodBrothers: z.array(familyLinkSchema).optional(),
  linkedHalfBrothers: z.array(familyLinkSchema).optional(),
  linkedStepBrothers: z.array(familyLinkSchema).optional(),
  linkedDaughters: z.array(familyLinkSchema).optional(),
  linkedSons: z.array(familyLinkSchema).optional(),
  linkedOthers: z.array(familyLinkSchema).optional(),

  /** Number of financial dependents */
  numberOfDependents: z.number().int().min(0).max(50).optional(),

  /** Housing type */
  housingType: z
    .enum(['own_house', 'rented', 'government_quarters', 'shared', 'homeless', 'other'])
    .optional(),

  /** Household income range (monthly) */
  householdIncome: z.string().trim().optional(),

  /** Below Poverty Line card */
  bplCard: z.enum(['yes', 'no', 'applied', 'dont_know']).optional(),

  /** Digital access (multi-select string array) */
  digitalAccess: z.array(z.string().trim()).optional(),

  /** Migration status */
  migrationStatus: z
    .enum(['native', 'within_district', 'within_state', 'inter_state', 'international'])
    .optional(),

  /** Land ownership */
  landOwnership: z
    .enum([
      'own_agricultural',
      'own_residential',
      'government_allotted',
      'no_land',
      'leased',
      'other',
    ])
    .optional(),

  /** Primary income source */
  primaryIncomeSource: z
    .enum([
      'agriculture',
      'government_job',
      'private_job',
      'business',
      'daily_wage',
      'pension',
      'remittance',
      'no_fixed_income',
      'other',
    ])
    .optional(),
});

// ─── Inferred types ─────────────────────────────────────────────────────────

export type Step12HouseholdInput = z.infer<typeof step12HouseholdSchema>;
export type FamilyLink = z.infer<typeof familyLinkSchema>;
